import React from 'react';
import { Grid, Paper, Typography, Box, Skeleton } from '@mui/material';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    BarChart, Bar, PieChart, Pie, Cell, Legend
} from 'recharts';
import { useSelector } from 'react-redux';
import { formatCurrency } from 'utils/function';
import CustomTypography from 'components/Fileds/CustomTypography';

const COLORS = ['#000000', '#565656', '#B0B0B0', '#E5E5E5'];

const ChartContainer = ({ title, children, loading, hasData = true }) => (
    <Paper className='dashboard-border' sx={{ p: 2, height: 350, display: 'flex', flexDirection: 'column', boxShadow: 'none' }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
            {title}
        </Typography>
        <Box sx={{ flexGrow: 1, width: '100%', minHeight: 0, position: 'relative' }}>
            {loading ? (
                <Skeleton variant="rectangular" width="100%" height="100%" />
            ) : !hasData ? (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    color: 'text.secondary'
                }}>
                    <Typography variant="body1">No Data Available</Typography>
                </Box>
            ) : (
                children
            )}
        </Box>
    </Paper>
);

// Custom Tooltip for Revenue Chart
const CustomRevenueTooltip = ({ active, payload, label, currency }) => {
    if (active && payload && payload.length) {
        return (
            <Box sx={{
                backgroundColor: 'white',
                border: '1px solid #ccc',
                padding: '10px',
                borderRadius: '4px'
            }}>
                <CustomTypography
                    variant="body2" sx={{ mb: 0 }}
                    label={label}
                />
                <CustomTypography
                    variant="body2" sx={{ color: '#000' }}
                    label={`Revenue: ${formatCurrency(payload[0]?.value, currency)}`}
                />
            </Box>
        );
    }
    return null;
};

const DashboardCharts = ({ data, loading }) => {
    // Get currency from Redux
    // const currency = useSelector((state) => state?.posData?.userData?.vendor?.currency);
    const currency = useSelector((state) => state?.posData?.vendor?.currency);


    // Transform data for charts if backend format is raw.
    // Assuming backend returns:
    // ordersOverTime: [{ date: '2023-01-01', count: 10 }, ...]
    // revenueOverTime: [{ date: '2023-01-01', revenue: 100 }, ...]
    // orderTypeDistribution: [{ order_type: 'dine_in', count: 50 }, ...]
    // peakOrderTime: [{ hour: 14, count: 20 }, ...]

    const ordersData = data?.ordersOverTime || [];
    const revenueData = data?.revenueOverTime || [];
    const typeData = data?.orderTypeDistribution?.map(item => ({
        name: item.order_type === 'pos' ? 'Takeaway' :
            item.order_type === 'dine_in' ? 'Dine-In' :
                item.order_type === 'booking' ? 'Scheduled' : item.order_type,
        value: parseInt(item.count)
    })) || [];
    const peakTimeData = data?.peakOrderTime?.map(item => ({
        hour: `${item.hour}:00`,
        count: parseInt(item.count) || 0
    })) || [];

    return (
        <Grid container spacing={3}>
            {/* Orders Over Time */}
            <Grid item xs={12} md={6}>
                <ChartContainer title="Orders Over Time" loading={loading} hasData={ordersData.length > 0}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={ordersData}>
                            <CartesianGrid vertical={false} strokeDasharray="3 3" />
                            <XAxis
                                dataKey="date"
                                tickLine={false}
                                axisLine={false}
                                tick={{ fontSize: 12 }}
                                tickFormatter={(val) => {
                                    // If it's a date string (YYYY-MM-DD), format it.
                                    // If it's "Jan", "Week-1", show as is.
                                    const date = new Date(val);
                                    if (!isNaN(date.getTime()) && val.length > 7) { // Check length to avoid "Jan" being parsed as date if 1st jan current year by browser quirks? unique check.
                                        // Actually "Jan" might be parsed as Date.
                                        // Better check: does it look like YYYY-MM-DD?
                                        if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
                                            return date.toLocaleDateString(undefined, { weekday: 'short' });
                                        }
                                    }
                                    return val;
                                }}
                            />
                            <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 12 }} />
                            <Tooltip />
                            <Line type="monotone" dataKey="count" stroke="#000000" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </ChartContainer>
            </Grid>

            {/* Revenue Over Time */}
            <Grid item xs={12} md={6}>
                <ChartContainer title="Revenue Over Time" loading={loading} hasData={revenueData.length > 0}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={revenueData} barSize={40}>
                            <CartesianGrid vertical={false} strokeDasharray="3 3" />
                            <XAxis
                                dataKey="date"
                                tickLine={false}
                                axisLine={false}
                                tick={{ fontSize: 12 }}
                                tickFormatter={(val) => {
                                    const date = new Date(val);
                                    if (!isNaN(date.getTime()) && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
                                        return date.toLocaleDateString(undefined, { weekday: 'short' });
                                    }
                                    return val;
                                }}
                            />
                            <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 12 }} />
                            <Tooltip content={<CustomRevenueTooltip currency={currency} />} />
                            <Bar dataKey="revenue" fill="#000000" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartContainer>
            </Grid>

            {/* Order Type Distribution */}
            <Grid item xs={12} md={6}>
                <ChartContainer title="Order Type Distribution" loading={loading} hasData={typeData.length > 0}>
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={typeData}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={80}
                                fill="#8884d8"
                                paddingAngle={2}
                                dataKey="value"
                            >
                                {typeData.map((entry, index) => (
                                    <Cell key={`cell - ${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                            <Legend verticalAlign="bottom" height={36} />
                        </PieChart>
                    </ResponsiveContainer>
                </ChartContainer>
            </Grid>

            {/* Peak Order Time */}
            <Grid item xs={12} md={6}>
                <ChartContainer title="Peak Order Time" loading={loading} hasData={peakTimeData.length > 0}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={peakTimeData || []} barSize={30}>
                            <CartesianGrid vertical={false} strokeDasharray="3 3" />
                            <XAxis dataKey="hour" tickLine={false} axisLine={false} tick={{ fontSize: 12 }} />
                            <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 12 }} />
                            <Tooltip cursor={{ fill: 'transparent' }} />
                            {/* Highlight logic could be complex, simplicity first */}
                            <Bar dataKey="count" fill="#565656" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartContainer>
            </Grid>
        </Grid>
    );
};

export default DashboardCharts;
